import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus } from "lucide-react";

export const FAQS = [
  {
    q: "What is hostile vehicle mitigation (HVM)?",
    a: "HVM is the use of physical security measures — bollards, road blockers, wedge barriers, crash-rated fencing and barrier arms — to stop vehicle-borne threats before they reach people, crowded places or critical infrastructure. A proper HVM solution starts with a threat assessment and ends with a crash-test-certified barrier, not a catalogue guess.",
  },
  {
    q: "What is ISO 22343 and why does it matter?",
    a: "ISO 22343-1:2023 is the current international standard for vehicle security barriers, introduced in September 2023. It defines how barriers are physically crash-tested and rated — for example V/7200[N2A]/80/90, meaning a 7,200 kg truck at 80 km/h at a 90° angle. New specifications in New Zealand and Australia should reference ISO 22343.",
  },
  {
    q: "Are PAS 68 and IWA 14-1 still valid?",
    a: "PAS 68 and IWA 14-1 were officially replaced by ISO 22343 in September 2023, but certificates issued under them remain valid proof of performance. Many existing specifications still cite PAS 68 — products certified to it (like the TiSO RB333-01 road blocker) remain fully legitimate choices.",
  },
  {
    q: "What is the difference between certified and estimated performance?",
    a: "Certified performance means the barrier survived an instrumented physical crash test with a published report. Estimated or 'engineered to resist' claims are calculations without a crash test. For government, stadium and crowded-place projects, specifiers should insist on certified ratings — every rating Access X quotes comes from a real crash test.",
  },
  {
    q: "How deep do HVM foundations need to be?",
    a: "It depends on the product. Ultra-shallow road blockers like the TiSO RB333-01 need only 200 mm of foundation depth, making them ideal above buried services. Traditional automatic bollards need deeper foundations. Surface-mounted options like the ARX STOPPER! need no foundations at all. This is why HVM is never one-size-fits-all.",
  },
  {
    q: "Do you supply and install across New Zealand and Australia?",
    a: "Yes. Access X Oceania is based in Auckland, NZ and Melbourne, AU, supplying HVM projects across both countries. Installation is handled by our certified partners in your area — which means local warranty, servicing and support long after handover. Over 70 product lines from TiSO, Automatic Systems, AccessX Fencing, HySecurity | Nice and ARX Security.",
  },
];

export const Faq = () => {
  const [open, setOpen] = useState(0);

  return (
    <section id="faq" data-testid="faq-section" className="relative py-24 sm:py-32 bg-[#0E1420] border-y border-[#26334D] noise-overlay">
      <div className="max-w-4xl mx-auto px-5 sm:px-8">
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="font-tech text-[11px] tracking-[0.3em] uppercase text-[#F4A031]"
        >
          Questions specifiers ask
        </motion.p>
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.1 }}
          className="font-display font-bold uppercase text-4xl sm:text-5xl leading-[0.95] tracking-tight mt-4"
        >
          HVM, <span className="text-[#F4A031]">answered.</span>
        </motion.h2>

        <div className="mt-12 border-t border-[#26334D]">
          {FAQS.map((f, i) => (
            <motion.div
              key={f.q}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ delay: i * 0.05 }}
              className="border-b border-[#26334D]"
            >
              <button
                data-testid={`faq-question-${i}`}
                onClick={() => setOpen(open === i ? -1 : i)}
                aria-expanded={open === i}
                className="w-full flex items-center justify-between gap-6 py-6 text-left group"
              >
                <span className={`font-grotesk font-bold text-lg transition-colors ${open === i ? "text-[#F4A031]" : "text-white group-hover:text-[#F4A031]"}`}>
                  {f.q}
                </span>
                <Plus
                  size={18}
                  className={`shrink-0 text-[#F4A031] transition-transform duration-300 ${open === i ? "rotate-45" : ""}`}
                />
              </button>
              <AnimatePresence initial={false}>
                {open === i && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
                    className="overflow-hidden"
                  >
                    <p data-testid={`faq-answer-${i}`} className="text-[#94A3B8] leading-relaxed pb-6 pr-10">
                      {f.a}
                    </p>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
